import { useOutletContext } from 'react-router-dom'

import { Box, Button, TagColor, Text } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { currencyToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import { usePaymentTotals } from 'modules/payroll_bundle/hooks/usePaymentTotals'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'

import PaymentOverviewCard from './PaymentOverviewCard'

interface Props {
  currency: Currency
}

const Overview = ({ currency }: Props) => {
  const { paymentDispatch } = usePayrollPayments()
  const { currentPeriod, currentPeriodStatus } = usePolicyPeriods()
  const currentPolicyPeriodId = currentPeriod?.id
  const { totals } = usePaymentTotals(currentPolicyPeriodId)

  const { filteredEmployees } = useOutletContext<{
    employees: Employee[]
    filteredEmployees: Employee[]
  }>()

  const startPaymentsGeneration = () => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        showSelectConceptModal: true,
      },
    })

    // Maybe we want to say for which kind of concept here?
    track(trackingEvents.payroll.generatePayment, {
      period_status: currentPeriodStatus,
    })
  }

  const amountOrDash = (amount: number, isFinancial?: boolean) => {
    if (!amount) {
      return '—'
    }

    if (isFinancial) {
      return formatAmount(currencyToNumber(amount), currency)
    }

    return amount
  }

  const totalEmployesInCurrentPeriod = filteredEmployees.length

  const toBePaidTotals = totals.find((total) => total.status === 'to_be_paid')
  const totalAmountToBePaid = toBePaidTotals?.total ?? 0
  const numberOfEmployeesToBePaid = toBePaidTotals?.employeeIds.length ?? 0

  const pendingTotals = totals.find((total) => total.status === 'pending')
  const totalPendingAmount = pendingTotals?.total ?? 0
  const numberOfEmployeesPendingToPay = pendingTotals?.employeeIds.length ?? 0

  const paidTotals = totals.find((total) => total.status === 'paid')
  const totalPaidAmount = paidTotals?.total ?? 0
  const numberOfEmployeesPaid = paidTotals?.employeeIds.length ?? 0

  return (
    <Box gap="s24" flexDirection="row" justifyContent="spaceBetween" className="payroll-overview">
      <PaymentOverviewCard
        statusLabel={i18n.t('payroll_bundle.results_payments.payments.overview.paid')}
        statusColor={TagColor.viridian}
        amountLabel={i18n.t('payroll_bundle.results_payments.payments.overview.total_amount')}
        amountValue={amountOrDash(totalPaidAmount, true)}
        countLabel={i18n.t('payroll_bundle.results_payments.payments.overview.employees')}
        countValue={amountOrDash(numberOfEmployeesPaid)}
        totalEmployees={totalEmployesInCurrentPeriod}
      />
      <PaymentOverviewCard
        statusLabel={i18n.t('payroll_bundle.results_payments.payments.overview.pending')}
        statusColor={numberOfEmployeesPendingToPay > 0 ? TagColor.champagne : TagColor.neutral}
        amountLabel={i18n.t('payroll_bundle.results_payments.payments.overview.total_amount')}
        amountValue={amountOrDash(totalPendingAmount, true)}
        countLabel={i18n.t('payroll_bundle.results_payments.payments.overview.employees')}
        countValue={amountOrDash(numberOfEmployeesPendingToPay)}
      />
      <Box
        border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
        borderRadius={{ all: 'abs008' }}
        flexDirection="column"
        padding="s24"
        gap="s24"
        width="s300"
        justifyContent="spaceAround"
        alignItems="flexStart"
        flexGrow
        className="payroll-overview-stats payroll-overview-stats--to-be-paid"
      >
        <Text.Normal>
          {numberOfEmployeesToBePaid > 0 &&
            i18n.tView('payroll_bundle.results_payments.payments.overview.left_to_be_paid', {
              count: (
                <strong>
                  {i18n.tp(
                    'payroll_bundle.results_payments.payments.overview.left_to_be_paid_employee_count',
                    {
                      count: numberOfEmployeesToBePaid,
                      amount: formatAmount(currencyToNumber(totalAmountToBePaid), currency),
                    }
                  )}
                </strong>
              ),
            })}
          {numberOfEmployeesToBePaid <= 0 && (
            <>{i18n.t('payroll_bundle.results_payments.payments.overview.none_left_to_be_paid')}</>
          )}
        </Text.Normal>

        {numberOfEmployeesToBePaid > 0 && (
          <Button onClick={startPaymentsGeneration}>
            {i18n.t('payroll_bundle.results_payments.payments.generate_new_payment')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default observer(Overview)
